import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Search, Plus, Loader2, AlertTriangle, Building2, Mail, Clock, XCircle, School as SchoolIcon } from 'lucide-react'; 
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { IssueSidebar } from '@/components/issues/IssueSidebar';
import { Modal } from '@/components/ui/modal';
import { IssueCard } from '@/components/issues/IssueCard';
import { AISummary } from '@/components/issues/AISummary';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import type { ReportedIssue, School, Grade } from '@/types';

const statusFilters = [
  { value: 'all', label: 'All Status', color: 'bg-zinc-100 hover:bg-zinc-200 text-zinc-900' },
  { value: 'new', label: 'New', color: 'bg-blue-100 hover:bg-blue-200 text-blue-900' },
  { value: 'in review', label: 'In Review', color: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-900' },
  { value: 'on hold', label: 'On Hold', color: 'bg-orange-100 hover:bg-orange-200 text-orange-900' },
  { value: 'resolved', label: 'Resolved', color: 'bg-green-100 hover:bg-green-200 text-green-900' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 hover:bg-red-200 text-red-900' },
] as const;

const statusColors = {
  new: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  'in review': 'bg-yellow-50 text-yellow-700 ring-yellow-700/10',
  'on hold': 'bg-orange-50 text-orange-700 ring-orange-700/10',
  resolved: 'bg-green-50 text-green-700 ring-green-700/10',
  rejected: 'bg-red-50 text-red-700 ring-red-700/10',
} as const;

interface Filters {
  search: string;
  status: string;
  school_id: string | null;
  grade: string | null;
  date: Date | undefined;
}

export default function ReportedIssues() {
  const navigate = useNavigate();
  const location = useLocation();
  const [issues, setIssues] = useState<ReportedIssue[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIssue, setSelectedIssue] = useState<ReportedIssue | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState<Record<string, any[]>>({});
  const [schoolSearch, setSchoolSearch] = useState('');
  const [commandOpen, setCommandOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    status: 'all',
    school_id: null,
    grade: null,
    date: undefined,
  });

  const resetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      school_id: null,
      grade: null,
      date: undefined,
    });
    setSchoolSearch('');
  };

  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    // Set selected issue from location state if available
    const selectedIssueId = location.state?.selectedIssueId;
    if (selectedIssueId) {
      const issue = issues.find(i => i.id === selectedIssueId);
      if (issue) {
        setSelectedIssue(issue);
        // Clear the location state
        window.history.replaceState({}, document.title);
      }
    }
  }, [issues, location.state]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch issues with notification logs
      const { data: issuesData, error: issuesError } = await supabase
        .from('sms_reported_issues')
        .select(`
          *,
          school:sms_schools(name),
          grade:sms_grades(name),
          notification_logs:sms_notification_logs(*)
        `)
        .order('created_at', { ascending: false });

      if (issuesError) throw issuesError;
      
      // Organize notification logs by issue
      const logsMap = {};
      if (issuesData) {
        issuesData.forEach((issue: any) => {
          logsMap[issue.id] = issue.notification_logs || [];
        });
      }
      
      setNotificationLogs(logsMap);
      setIssues(issuesData || []);

      // Fetch schools
      const { data: schoolsData } = await supabase
        .from('sms_schools')
        .select('*')
        .order('name');
      if (!schoolsData?.error) setSchools(schoolsData || []);

      // Fetch grades
      const { data: gradesData } = await supabase
        .from('sms_grades')
        .select('*')
        .order('name');
      if (!gradesData?.error) setGrades(gradesData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedIssue) return;

    try {
      const { error } = await supabase
        .from('sms_reported_issues')
        .delete()
        .eq('id', selectedIssue.id);

      if (error) throw error;

      toast.success('Issue deleted successfully');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting issue:', error);
      toast.error('Failed to delete issue');
    }
  };

  const filteredIssues = issues.filter((issue) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = 
      issue.issue_number.toLowerCase().includes(searchLower) ||
      issue.teacher_name.toLowerCase().includes(searchLower) ||
      issue.subject.toLowerCase().includes(searchLower) ||
      issue.grade?.name.toLowerCase().includes(searchLower);

    const matchesStatus = filters.status === 'all' || issue.status === filters.status;
    const matchesSchool = filters.school_id === null || issue.school_id === filters.school_id;
    const matchesGrade = filters.grade === null || issue.grade_id === filters.grade;
    const matchesDate = !filters.date || 
      format(new Date(issue.created_at!), 'yyyy-MM-dd') === 
      format(filters.date, 'yyyy-MM-dd');

    return matchesSearch && matchesStatus && matchesSchool && matchesGrade && matchesDate;
  });

  return (
    <div className="container mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex-1 mb-4 md:mb-0">
          <h1 className="text-3xl font-bold">Reported Issues</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track reported teaching issues
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate('/')}>
            <Plus className="h-4 w-4 mr-2" />
            Report Issue
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search issues..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-9 w-full transition-all focus:ring-2"
            />
          </div>
          
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative w-full md:w-[300px]">
              <Input
                placeholder="Type to search for a school..."
                value={schoolSearch}
                onChange={(e) => {
                  setSchoolSearch(e.target.value);
                  setCommandOpen(true);
                }}
                onClick={() => setCommandOpen(true)}
                className="w-full"
              />
              {commandOpen && (
                <div className="absolute w-full z-50 top-full mt-1">
                  <Card>
                    <Command className="rounded-lg border shadow-md">
                      <CommandGroup className="max-h-64 overflow-auto">
                        {schools
                          .filter(school =>
                            school.name.toLowerCase().includes(schoolSearch.toLowerCase())
                          )
                          .map((school) => (
                            <CommandItem
                              key={school.id}
                              onSelect={() => {
                                setFilters({
                                  ...filters,
                                  school_id: school.id
                                });
                                setSchoolSearch(school.name);
                                setCommandOpen(false);
                              }}
                              className="cursor-pointer"
                            >
                              <SchoolIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                              {school.name}
                            </CommandItem>
                          ))}
                        {schools.filter(school =>
                          school.name.toLowerCase().includes(schoolSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="p-2 text-sm text-muted-foreground">
                            School not found
                          </div>
                        )}
                      </CommandGroup>
                    </Command>
                  </Card>
                </div>
              )}
            </div>

            <Select
              value={filters.grade || undefined}
              onValueChange={(value) => setFilters({ ...filters, grade: value })}
            >
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Select grade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {grades.map((grade) => (
                  <SelectItem key={grade.id} value={grade.id}>
                    {grade.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn( 
                    'w-full md:w-[180px] justify-start text-left font-normal',
                    !filters.date && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.date ? format(filters.date, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={filters.date}
                  onSelect={(date) => setFilters({ ...filters, date })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="outline"
              onClick={resetFilters}
              className="gap-2"
              disabled={!filters.search && !filters.school_id && !filters.grade && !filters.date && filters.status === 'all'}
            >
              <XCircle className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 flex-wrap mt-4 overflow-x-auto pb-2">
          {statusFilters.map((status) => (
            <Button
              key={status.value}
              className={cn(
                'transition-colors',
                filters.status === status.value ? status.color : 'bg-transparent hover:bg-zinc-100'
              )}
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ ...filters, status: status.value })}
            >
              {status.label}
            </Button>
          ))}
        </div>
      </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
      <div className="lg:col-span-2 order-2 lg:order-1">
        <Card>
          <CardContent className="p-6">
            <AISummary 
              issues={filteredIssues} 
              isFiltered={
                !!filters.search || 
                filters.status !== 'all' || 
                !!filters.school_id || 
                !!filters.grade || 
                !!filters.date
              }
              filters={filters}
            />
            <div className="space-y-4">
            {/* Results Count */}
            <div className="text-sm text-muted-foreground">
              Found {filteredIssues.length} issue{filteredIssues.length !== 1 ? 's' : ''}
            </div>

            {/* Issues List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredIssues.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No issues found
              </div>
            ) : (
              <div className="space-y-4">
                {filteredIssues.map((issue) => (
                  <div
                    key={issue.id} 
                    className={cn(
                      "flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors",
                      selectedIssue?.id === issue.id
                        ? "bg-primary/5 border-primary shadow-sm"
                        : "hover:bg-accent/5"
                    )}
                    onClick={() => setSelectedIssue(issue)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className={`h-4 w-4 ${
                          issue.status === 'new' ? 'text-blue-500' :
                          issue.status === 'in review' ? 'text-yellow-500' :
                          issue.status === 'on hold' ? 'text-orange-500' :
                          issue.status === 'resolved' ? 'text-green-500' :
                          'text-red-500'
                        }`} />
                        <code className="text-xs font-mono bg-zinc-100 px-2 py-1 rounded">
                          #{issue.issue_number}
                        </code>
                        <Badge
                          variant="outline"
                          className={`${statusColors[issue.status as keyof typeof statusColors]}`}
                        >
                          {issue.status}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <h3 className="text-sm font-medium truncate">
                            {issue.school?.name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">{issue.grade?.name}</span>
                          <span className="text-muted-foreground">•</span>
                          <span>{issue.teacher_name}</span>
                          <span className="text-muted-foreground">•</span>
                          <span className="text-muted-foreground">{issue.subject}</span>
                        </div>
                        {notificationLogs[issue.id]?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {notificationLogs[issue.id].filter(log => log.status === 'sent').length} notification(s) sent
                            </span>
                          </div>
                        )}
                      </div>
                      {selectedIssue?.id === issue.id && (
                        <div className="mt-4 p-4">
                          <h4 className="text-sm font-medium mb-2">Issue Details</h4>
                          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                            {issue.details}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
                      <Clock className="h-4 w-4" />
                      {format(new Date(issue.created_at!), 'MMM d, yyyy')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
      
      <div className="lg:col-span-1">
        <IssueSidebar
          issue={selectedIssue}
          notificationLogs={selectedIssue ? (notificationLogs[selectedIssue.id] || []) : []}
          onUpdate={fetchData}
          onClose={() => setSelectedIssue(null)}
        />
      </div>
    </div>

    <Modal
      title="Delete Issue"
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      onConfirm={handleDelete}
      confirmText="Delete"
      confirmVariant="destructive"
    >
      <p>Are you sure you want to delete this issue? This action cannot be undone.</p>
    </Modal>
  </div>
);
}